/**
 * Entry point.
 *
 * Ansvar holdes tynt: sette opp kartet og koble sammen modulene.
 * Kartstil, lag, kamera og data ligger i separate filer.
 */

import { Map as MaplibreMap, NavigationControl, addProtocol } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import './map/worker.js'; // setWorkerUrl før første kart (maplibre v6)
import './style.css';

import { buildStyle } from './map/style.js';
import { FIRST_LABEL_LAYER_ID, CLOCKTOWER_LNGLAT } from './map/layers.js';
import { INITIAL_CAMERA, wireCameraControls } from './map/camera.js';
import { addRoutes, ROUTE_COLORS, ROUTE_SAMPLERS, setRouteVisibility, setTunnelVisibility } from './map/route.js';
import { createModelLayer } from './map/models.js';
import { createMarkerLayer, wireMarkerControls } from './map/markers.js';
import { enableTerrain, TERRAIN_EXAGGERATION } from './map/terrain.js';
import { addPlaceSigns } from './map/place-signs.js';
import { createCameraHud } from './map/camera-hud.js';
import { wireThemeControls, applyPalette } from './map/theme.js';
import { wireIntro } from './map/intro.js';
import { setupRace, formatClock } from './map/race.js';
import { MAX_BOUNDS } from './data/bounds.js';
import { enableClickInspector } from './map/debug.js';
import {
  BUILDING_PATCH_PROTOCOL,
  loadBuildingTile,
  transformBuildingTileRequest,
} from './map/building-tile-protocol.js';

// Edit the one small footprint in the incoming vector tile, before extrusion.
addProtocol(BUILDING_PATCH_PROTOCOL, loadBuildingTile);

const map = new MaplibreMap({
  container: 'map',
  style: buildStyle(),
  transformRequest: transformBuildingTileRequest,
  center: INITIAL_CAMERA.center,
  zoom: INITIAL_CAMERA.zoom,
  pitch: INITIAL_CAMERA.pitch,
  bearing: INITIAL_CAMERA.bearing,
  maxBounds: MAX_BOUNDS,
  minZoom: 11,
  maxZoom: 25,
  maxPitch: 80,
  attributionControl: { compact: true },
});

map.addControl(new NavigationControl({ visualizePitch: true }), 'bottom-right');

// Markørene samles her så de kan styres utenfra (animasjon senere, og
// konsollen under utvikling). map.getLayer() gir MapLibres interne
// lagobjekt, ikke vårt custom layer, så vi holder referansene selv.
export const MARKERS = {};

map.on('load', () => {
  // Kartverket-terrenget. Må stå FØR rutene og modellene legges til, slik at
  // linjelagene drapes på høydene med én gang og modellene finner bakken.
  enableTerrain(map, FIRST_LABEL_LAYER_ID);

  // Legg begge rutene under labels slik at stedsnavn ligger øverst.
  addRoutes(map, FIRST_LABEL_LAYER_ID);

  // Klokketårnet (Aker Brygge) som 3D-modell, kun på høy zoom.
  map.addLayer(
    createModelLayer({
      id: 'clocktower',
      url: import.meta.env.BASE_URL + 'models/clocktower_stylized.glb',
      lngLat: CLOCKTOWER_LNGLAT,
      altitude: 0, // står på brygga ved havnivå
      rotationDeg: 0,
      scale: 1, // ekte meter (høyde 9.063 m)
      minzoom: 15,
    }),
  );

  // Busskur (Guldbrandslia, start på grønn rute) som 3D-modell.
  map.addLayer(
    createModelLayer({
      id: 'bus-shelter',
      url: import.meta.env.BASE_URL + 'models/bus_shelter_stylized.glb',
      lngLat: [10.506425, 59.942853],
      altitude: 0,
      rotationDeg: 136, // parallelt med veien, riktig vei (226°+90°+180°)
      scale: 1, // ekte meter (~2.69 m høyt)
      minzoom: 15,
    }),
  );

  // Stort busskur: 50x større, samme plassering. Skjult som default, slås
  // på med egen avkrysningsboks i preview.
  const bigShelter = createModelLayer({
    id: 'bus-shelter-big',
    url: import.meta.env.BASE_URL + 'models/bus_shelter_stylized.glb',
    lngLat: [10.506425, 59.942853],
    altitude: 0,
    rotationDeg: 136,
    scale: 100, // 100x
    minzoom: 11, // synlig også på vidvinkel siden den er stor
    visible: false,
  });
  map.addLayer(bigShelter);
  const bigShelterBox = document.querySelector('#show-shelter-big');
  if (bigShelterBox) {
    bigShelter.setVisible(bigShelterBox.checked);
    bigShelterBox.addEventListener('change', () => bigShelter.setVisible(bigShelterBox.checked));
  }

  // Stedsnavnskilt for rutas endepunkter. Lagt etter modellene så de
  // tegnes over dem, men under stedsnavn-labels.
  addPlaceSigns(map);

  wireCameraControls(map);

  // Live kamera-avlesning + «Kopier flyTo». Brukes til å finne og notere
  // kamerastater for animasjonen.
  createCameraHud(map, document.querySelector('#camera-hud'), document.querySelector('#hud-toggle'));

  // Palettvelger + lysskyvere. Må kjøre SIST: applyPalette leser lagene som
  // allerede ligger i kartet, så rutelagene må være lagt til først.
  wireThemeControls(map, document);

  // Kjøretøy + følgemarkører + scene 8 (race). Delt oppsett med race-embedden
  // via setupRace, så fartsprofiler/distanser bor ett sted (src/map/race.js).
  const raceClock = document.querySelector('#race-clock');
  const { scene8, busVehicle, busMarker } = setupRace({
    map,
    exaggeration: TERRAIN_EXAGGERATION,
    busUrl: import.meta.env.BASE_URL + 'models/ruter_bus_stylized.glb',
    carUrl: import.meta.env.BASE_URL + 'models/sports_car_stylized.glb',
    busId: 'intro-bus',
    carId: 'scene-car',
    onTime: (realMs) => {
      if (raceClock) raceClock.textContent = formatClock(realMs);
    },
  });

  // Intro 7: buss som kjører langs ruta med grønn strek bak + følgemarkør.
  // Gjenbruker samme buss-vehicle/-markør som racet.
  const busLength = ROUTE_SAMPLERS.bus.length;
  const introCallbacks = {
    onStart: (variant) => {
      const on = variant === 'intro7' || variant === 'intro7mobile';
      busVehicle.setHidden(!on);
      busMarker.setVisible(on);
    },
    onEnd: () => {
      busVehicle.setHidden(true);
      busMarker.setVisible(false);
    },
    onDraw: (t) => {
      // Flytt buss + markør til samme fremdrift som den grønne streken.
      const p = busVehicle.setDistance(t * busLength);
      busMarker.setLngLat(p.lngLat, (p.elevation ?? 0) * TERRAIN_EXAGGERATION);
      busMarker.setHidden(p.structure === 'tunnel');
    },
  };

  // Vis race-klokka mens scene 8 spiller, skjul den når man stopper.
  const scene8WithClock = {
    label: scene8.label,
    play() {
      if (raceClock) {
        raceClock.textContent = formatClock(0);
        raceClock.hidden = false;
      }
      scene8.play();
    },
    stop() {
      scene8.stop();
      if (raceClock) raceClock.hidden = true;
    },
  };
  introCallbacks.custom = { scene8: scene8WithClock };

  // Intro-animasjonen (nedtrekksmeny + spill-knapp). Må stå etter addRoutes.
  wireIntro(map, document.querySelector('#intro-select'), document.querySelector('#intro-play'), introCallbacks);

  // Kjøretøy-markører ved rutestart (grønn buss, gul bil). Følger fronten
  // av ruta senere; foreløpig statisk ved start. Bil tegnes først.
  //
  // Bakt høyde er fallback til DEM er lastet. Markørlaget tar hensyn til
  // terrengets overdrivelse og følger terrenghøyden når detaljnivået endres.
  for (const mode of ['car', 'bus']) {
    const start = ROUTE_SAMPLERS[mode].at(0);
    const marker = createMarkerLayer({
      id: `marker-${mode}`,
      mode,
      lngLat: start.lngLat,
      color: ROUTE_COLORS[mode].line,
    });
    map.addLayer(marker);
    marker.setLngLat(start.lngLat, start.elevation);
    marker.setHidden(start.structure === 'tunnel');
    MARKERS[mode] = marker;
  }
  wireMarkerControls(MARKERS);

  // Klikk-for-koordinat: dev-siden er redigeringsverktøyet vårt. Klikk på
  // kartet → [lng, lat] + feature-info i konsollen. Embed-sidene (de faktiske
  // web-produktene) importerer ikke denne.
  enableClickInspector(map);

  // MINIMAL-MODUS (?minimal): strippet visning for å kalibrere mobil-kameraer.
  // Skjuler alt UI unntatt kamera-HUD-en («Kopier kamera»), viser kun
  // bussruta, og bruker DN Blue Mono – et rent bilde å finne utsnitt i.
  if (new URLSearchParams(location.search).has('minimal')) {
    document.querySelector('#camera-controls')?.style.setProperty('display', 'none');
    document.querySelector('#light-controls')?.style.setProperty('display', 'none');
    setRouteVisibility(map, 'car', false);
    setTunnelVisibility(map, false);
    applyPalette(map, 'bluemono');
  }
});

// Gjør kartet tilgjengelig i konsollen under utvikling.
//
// MERK: <div id="map"> gjør at nettleseren allerede har en global `map` som
// peker på DIV-en, ikke på kartet. Den eksplisitte tilordningen her overstyrer
// den, så `map.flyTo(...)` virker som forventet i konsollen.
if (import.meta.env.DEV) {
  window.map = map;
  window.ROUTE_SAMPLERS = ROUTE_SAMPLERS;
  window.MARKERS = MARKERS;
}

// Logg feil tydelig i konsollen under utvikling.
map.on('error', (e) => {
  console.error('[maplibre error]', e && e.error ? e.error : e);
});
